// CHUNK 6: Form to create a synastry reading (manual data entry or invite link flow).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BirthDateInput } from '../ui/BirthDateInput';
import { BirthTimeInput } from '../ui/BirthTimeInput';
import { CityAutocomplete } from '../ui/CityAutocomplete';

type SynastryType = 'romantic' | 'friendship' | 'professional' | 'family';
type Method       = 'manual' | 'invite';

interface Props {
  onInviteCreated?: (inviteLink: string) => void;
}

const TYPE_OPTIONS: { value: SynastryType; label: string; emoji: string }[] = [
  { value: 'romantic',     label: 'Romântico',     emoji: '💕' },
  { value: 'friendship',   label: 'Amizade',       emoji: '🤝' },
  { value: 'professional', label: 'Profissional',  emoji: '💼' },
  { value: 'family',       label: 'Familiar',      emoji: '✨' },
];

export function SynastryCreationForm({ onInviteCreated }: Props) {
  const navigate      = useNavigate();
  const [step,        setStep]        = useState<'method' | 'details'>('method');
  const [method,      setMethod]      = useState<Method>('invite');
  const [synType,     setSynType]     = useState<SynastryType>('romantic');
  const [partnerName, setPartnerName] = useState('');
  const [birthDate,   setBirthDate]   = useState('');
  const [birthTime,   setBirthTime]   = useState('');
  const [birthCity,   setBirthCity]   = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  async function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!partnerName || !birthDate) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('create-synastry', {
        body: {
          partnerName,
          partnerBirthDate: birthDate,
          partnerBirthTime: birthTime || undefined,
          partnerBirthCity: birthCity || undefined,
          synastryType:     synType,
          createInviteLink: false,
        },
      });
      if (fnErr || !data?.id) throw fnErr ?? new Error('Erro ao criar sinastria');
      navigate(`/sinastria/${data.id}`);
    } catch (err) {
      setError('Não foi possível gerar a análise. Tente novamente.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleInviteCreate() {
    setLoading(true);
    setError('');
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('create-synastry-invite', {
        body: { synastryType: synType },
      });
      if (fnErr || !data?.inviteLink) throw fnErr ?? new Error('Erro ao gerar convite');
      onInviteCreated?.(data.inviteLink as string);
      navigate(`/sinastria/${data.synastryId}?pending=1`);
    } catch (err) {
      setError('Não foi possível gerar o convite. Tente novamente.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  if (step === 'method') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <h2 className="text-[#C9A84C] text-2xl font-light tracking-wider mb-6 text-center">
          ✦ Análise de Sinastria
        </h2>

        {/* Method selection */}
        <div className="space-y-4 mb-6">
          {([
            {
              value:   'invite' as Method,
              title:   'Convidar alguém',
              desc:    'Gere um link e compartilhe. A pessoa preenche seus próprios dados e ambos veem o resultado.',
              badge:   '✨ Recomendado',
            },
            {
              value:   'manual' as Method,
              title:   'Entrada manual',
              desc:    'Preencha os dados da outra pessoa. Apenas você verá o resultado.',
              badge:   '🔒 Privado',
            },
          ] as const).map(opt => (
            <button
              key={opt.value}
              onClick={() => setMethod(opt.value)}
              className={`w-full text-left border rounded-sm p-4 transition-colors ${
                method === opt.value
                  ? 'border-[#C9A84C] bg-[#C9A84C]/10'
                  : 'border-[#C9A84C]/20 hover:border-[#C9A84C]/40'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[#F5ECD7] text-sm font-medium">{opt.title}</div>
                  <div className="text-[#F5ECD7]/60 text-xs mt-1 leading-relaxed">{opt.desc}</div>
                </div>
                <span className="text-[#C9A84C] text-xs ml-3 flex-shrink-0">{opt.badge}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Synastry type */}
        <div className="mb-6">
          <div className="text-[#F5ECD7]/60 text-xs uppercase tracking-wider mb-3">Tipo de relacionamento</div>
          <div className="grid grid-cols-2 gap-3">
            {TYPE_OPTIONS.map(t => (
              <button
                key={t.value}
                onClick={() => setSynType(t.value)}
                className={`border rounded-sm p-3 text-sm transition-colors ${
                  synType === t.value
                    ? 'border-[#C9A84C] text-[#C9A84C]'
                    : 'border-[#C9A84C]/20 text-[#F5ECD7]/70 hover:border-[#C9A84C]/40'
                }`}
              >
                {t.emoji} {t.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => method === 'invite' ? handleInviteCreate() : setStep('details')}
          disabled={loading}
          className="w-full py-4 bg-[#C9A84C] text-[#0D1130] text-xs uppercase tracking-[2px] font-bold hover:bg-[#C9A84C]/90 disabled:opacity-60 transition-colors"
        >
          {loading ? 'Processando…' : method === 'invite' ? 'Gerar link de convite →' : 'Continuar →'}
        </button>

        {error && <p className="text-red-400 text-xs mt-3 text-center">{error}</p>}
      </div>
    );
  }

  // Step 2: Manual details form
  return (
    <div className="max-w-2xl mx-auto p-6">
      <h3 className="text-[#F5ECD7] text-lg font-light mb-6">Dados da outra pessoa</h3>

      {error && (
        <div className="mb-4 p-3 border border-red-500/40 bg-red-900/20 text-red-300 text-xs rounded-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleManualSubmit} className="space-y-4">
        <label className="block">
          <span className="block text-[#F5ECD7]/60 text-xs uppercase tracking-wider mb-2">Nome completo</span>
          <input
            type="text"
            value={partnerName}
            onChange={e => setPartnerName(e.target.value)}
            placeholder="Como você chama essa pessoa"
            required
            className="w-full bg-transparent border border-[#C9A84C]/30 text-[#F5ECD7] p-3 rounded-sm placeholder-[#F5ECD7]/30 focus:border-[#C9A84C] focus:outline-none"
          />
        </label>

        <div>
          <span className="block text-[#F5ECD7]/60 text-xs uppercase tracking-wider mb-2">Data de nascimento</span>
          <BirthDateInput value={birthDate} onChange={setBirthDate} required />
        </div>

        <div>
          <span className="block text-[#F5ECD7]/60 text-xs uppercase tracking-wider mb-2">Hora de nascimento</span>
          <BirthTimeInput value={birthTime} onChange={setBirthTime} />
        </div>

        <div>
          <span className="block text-[#F5ECD7]/60 text-xs uppercase tracking-wider mb-2">Cidade de nascimento</span>
          <CityAutocomplete value={birthCity} onChange={setBirthCity} placeholder="Cidade, Estado" />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => setStep('method')}
            className="px-6 py-3 text-[#F5ECD7]/50 text-xs uppercase tracking-wider hover:text-[#F5ECD7] transition-colors"
          >
            ← Voltar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-4 bg-[#C9A84C] text-[#0D1130] text-xs uppercase tracking-[2px] font-bold hover:bg-[#C9A84C]/90 disabled:opacity-60 transition-colors"
          >
            {loading ? 'Gerando análise…' : '✦ Gerar Sinastria ✦'}
          </button>
        </div>
      </form>
    </div>
  );
}
